<p-dialog
  [visible]="brochureAnalysisService.showBrochureAnalysisModal()"
  [modal]="true"
  [draggable]="false"
  [resizable]="false"
  [style]="{ width: '90%', maxWidth: '800px' }"
  [closable]="false"
  (onHide)="onHide()"
  styleClass="brochure-analysis-modal"
>
  <ng-template pTemplate="header">
    <div class="flex items-center gap-3">
      <div class="flex h-12 w-12 items-center justify-center rounded-full bg-primary-50 text-primary-500">
        <lucide-icon name="bot" [size]="24"></lucide-icon>
      </div>
      <span class="text-xl font-semibold text-greyscale-900">
        @if (brochureAnalysisService.state().status === "loading") {
          {{ "calculator.analyzing" | transloco }}
        } @else if (brochureAnalysisService.state().status === "success") {
          {{ "calculator.fileAnalysisSuccess" | transloco }}
        } @else {
          {{ "calculator.analyzeFile" | transloco }}
        }
      </span>
    </div>
  </ng-template>

  <!-- État de chargement -->
  @if (brochureAnalysisService.state().status === "loading") {
    <div class="flex flex-col items-center justify-center p-8 text-center">
      <app-greentripper-loader size="lg" [aiMode]="true" class="mb-4"></app-greentripper-loader>

      <div class="loading-message-container mb-4">
        <p [@fadeMessage]="'in'" class="text-lg font-medium text-gray-600">
          {{ loadingMessages[currentMessageIndex] }}
        </p>
      </div>

      <p class="text-sm text-gray-500">
        {{ "calculator.aiAnalysisProcessingTime" | transloco }}
      </p>
    </div>
  }

  <!-- État d'erreur -->
  @if (brochureAnalysisService.state().status === "error") {
    <div class="flex flex-col items-center justify-center p-8 text-center">
      <i class="pi pi-exclamation-triangle mb-4 text-5xl text-red-500"></i>
      <h3 class="mb-2 text-xl font-semibold">
        {{ "calculator.aiError.generic.title" | transloco }}
      </h3>
      <p class="mb-4 text-gray-600">
        {{ getTranslatedError().message }}
      </p>
      @if (getTranslatedError().suggestions && getTranslatedError().suggestions!.length > 0) {
        <div class="mb-4 rounded bg-yellow-50 p-4 text-left text-sm">
          <p class="mb-2 font-semibold text-yellow-800">
            {{ "calculator.aiError.suggestions" | transloco }}:
          </p>
          <ul class="list-inside list-disc text-yellow-700">
            @for (suggestion of getTranslatedError().suggestions; track $index) {
              <li>{{ suggestion }}</li>
            }
          </ul>
        </div>
      }
      <div class="flex justify-center">
        <p-button
          label="{{ 'calculator.backToCalculator' | transloco }}"
          styleClass="p-button-secondary"
          (onClick)="brochureAnalysisService.cancelAnalysis()"
        ></p-button>
      </div>
    </div>
  }

  <!-- État de succès -->
  @if (
    brochureAnalysisService.state().status === "success" &&
    brochureAnalysisService.state().result
  ) {
    <div class="results-container p-4" [@successAnimation]="'in'">
      <!-- Message d'avertissement -->
      <div class="mb-6 rounded bg-blue-50 p-3 text-sm text-blue-800">
        <p>
          <strong
            >{{ "calculator.aiAnalysisWarning.title" | transloco }}:</strong
          >
          {{ "calculator.aiAnalysisWarning.message" | transloco }}
          {{ "calculator.aiAnalysisWarning.editLater" | transloco }}
        </p>
      </div>

      <!-- Informations générales (condensées) -->
      <div class="mb-6 flex flex-wrap gap-4 text-sm">
        <div class="flex gap-2">
          <span class="font-medium text-greyscale-600">{{ "calculator.label.title" | transloco }}:</span>
          <span class="text-greyscale-900">{{ brochureAnalysisService.state().result?.title || "-" }}</span>
        </div>
        <div class="flex gap-2">
          <span class="font-medium text-greyscale-600">{{ "calculator.label.country" | transloco }}:</span>
          <span class="text-greyscale-900">{{ brochureAnalysisService.state().result?.countryCode || "-" }}</span>
        </div>
        <div class="flex gap-2">
          <span class="font-medium text-greyscale-600">{{ "calculator.label.days" | transloco }}:</span>
          <span class="text-greyscale-900">
            {{ brochureAnalysisService.state().result?.days || "-" }}
            {{ brochureAnalysisService.state().result?.days ? ("travelBoards.days" | transloco) : "" }}
          </span>
        </div>
        <div class="flex gap-2">
          <span class="font-medium text-greyscale-600">{{ "calculator.label.passengers" | transloco }}:</span>
          <span class="text-greyscale-900">
            {{ brochureAnalysisService.state().result?.travellers || "-" }}
            {{ brochureAnalysisService.state().result?.travellers ? ("travelBoards.people" | transloco) : "" }}
          </span>
        </div>
      </div>

      <!-- Section Prestation -->
      <section class="mb-6 flex flex-col gap-4">
        <h2 class="text-primary-900">{{ "calculator.prestation" | transloco }}</h2>

        <!-- Transports (pleine largeur) -->
        <div class="flex flex-col gap-4 rounded-lg bg-primary-50 p-4">
          <h3 class="text-primary-900">{{ "calculator.transport" | transloco }}</h3>

          @if (hasTransports) {
            <div class="flex flex-col gap-4">
              @for (
                transport of brochureAnalysisService.state().result?.transports;
                track $index
              ) {
                <article class="flex flex-col gap-3 rounded-lg bg-white p-4">
                  <div class="flex items-center justify-between">
                    <span class="paragraph-med text-primary-950">
                      {{ transport.title ?? getTransportTypeLabel(transport.type || "") }}
                    </span>
                    @if (transport.isMainTransport) {
                      <span class="rounded bg-blue-100 px-2 py-1 text-xs text-blue-800">
                        {{ "calculator.isMainTransport" | transloco }}
                      </span>
                    }
                  </div>

                  @if (transport.flightStops && transport.flightStops.length > 0) {
                    <div class="flex flex-col gap-1">
                      <div class="flex items-center gap-2 text-paragraph-med text-greyscale-700">
                        @for (airport of transport.flightStops; track $index) {
                          <span>{{ getFlightStopAirportCode(transport, $index) }}</span>
                          @if (airport.type !== "arrival") {
                            <span>→</span>
                          }
                        }
                      </div>
                      <span class="rounded-xl bg-gray-100 self-start px-2 py-1 text-paragraph-xs-reg">
                        {{ transport.return ? ("calculator.return" | transloco) : ("calculator.oneWay" | transloco) }}
                      </span>
                    </div>
                  } @else if (transport.roadStops && transport.roadStops.length > 0) {
                    <div class="flex flex-col gap-1">
                      <div class="flex flex-col text-paragraph-med text-greyscale-700">
                        <span class="truncate">{{ getRoadStopAddress(transport, 0) }}</span>
                        <span class="truncate">{{ getDestinationRoadStopAddress(transport) }}</span>
                      </div>
                      <div class="flex gap-2">
                        @if (transport.distance) {
                          <span class="text-paragraph-s-med text-greyscale-600">
                            {{ transport.distance }} km
                          </span>
                        }
                        <span class="rounded-xl bg-gray-100 px-2 py-1 text-paragraph-xs-reg">
                          {{ transport.return ? ("calculator.return" | transloco) : ("calculator.oneWay" | transloco) }}
                        </span>
                      </div>
                    </div>
                  } @else {
                    <div class="text-paragraph-s-reg text-greyscale-500">
                      {{
                        transport.distance
                          ? transport.distance + " km"
                          : transport.duration
                            ? transport.duration + " " + ("calculator.label.duration" | transloco)
                            : ("calculator.aiAnalysisResults.distanceOrDurationNotSpecified" | transloco)
                      }}
                    </div>
                  }
                </article>
              }
            </div>
          } @else {
            <div class="text-paragraph-s-reg italic text-greyscale-500">
              {{ "calculator.aiAnalysisResults.noTransportDetected" | transloco }}
            </div>
          }
        </div>

        <!-- Activity + Accommodation (côte à côte 50/50) -->
        <div class="flex flex-col gap-6 md:flex-row">
          <!-- Activities -->
          <div class="flex flex-1 flex-col gap-4 rounded-lg bg-primary-50 p-4">
            <h3 class="text-primary-900">{{ "calculator.activities" | transloco }}</h3>

            @if (hasActivities) {
              <div class="flex flex-col gap-4">
                @for (
                  activity of brochureAnalysisService.state().result?.activities;
                  track $index
                ) {
                  <article class="flex flex-col gap-3 rounded-lg bg-white p-4">
                    <div class="paragraph-med text-primary-950">
                      {{ activity.title ?? getActivityTypeLabel(activity.type || "") }}
                    </div>

                    @if (!isZeroImpactActivity(activity.type || "")) {
                      <div class="flex flex-col gap-1 text-paragraph-s-med">
                        @if (activity.days) {
                          <div class="flex gap-2">
                            <span class="text-greyscale-600">{{ "travelBoards.duration" | transloco }}:</span>
                            <span class="text-greyscale-900">
                              {{ activity.days }}
                              {{ activity.days > 1 ? ("tripDashboard.days" | transloco) : ("tripDashboard.day" | transloco) }}
                            </span>
                          </div>
                        }
                        @if (activity.kilometers) {
                          <div class="flex gap-2">
                            <span class="text-greyscale-600">{{ "tripDashboard.distance" | transloco }}:</span>
                            <span class="text-greyscale-900">{{ activity.kilometers }} km</span>
                          </div>
                        }
                        @if (activity.hours) {
                          <div class="flex gap-2">
                            <span class="text-greyscale-600">{{ "travelBoards.duration" | transloco }}:</span>
                            <span class="text-greyscale-900">{{ activity.hours }} {{ "calculator.label.hours" | transloco }}</span>
                          </div>
                        }
                      </div>
                    } @else {
                      <p class="text-paragraph-s-reg italic text-greyscale-500">
                        {{ "calculator.zeroImpactActivity" | transloco }}
                      </p>
                    }
                  </article>
                }
              </div>
            } @else {
              <div class="text-paragraph-s-reg italic text-greyscale-500">
                {{ "calculator.aiAnalysisResults.noActivityDetected" | transloco }}
              </div>
            }
          </div>

          <!-- Accommodations -->
          <div class="flex flex-1 flex-col gap-4 rounded-lg bg-primary-50 p-4">
            <h3 class="text-primary-900">{{ "calculator.accommodation" | transloco }}</h3>

            @if (hasAccommodations) {
              <div class="flex flex-col gap-4">
                @for (
                  accommodation of brochureAnalysisService.state().result?.accommodations;
                  track $index
                ) {
                  <article class="flex flex-col gap-3 rounded-lg bg-white p-4">
                    <div class="flex items-center gap-2">
                      <span class="paragraph-med text-primary-950">
                        {{ accommodation.title ?? getAccommodationTypeLabel(accommodation.type || "") }}
                      </span>
                      @if (accommodation.isEcoFriendly) {
                        <div class="rounded-lg bg-primary-100 px-2 py-1 text-primary-700">
                          {{ "calculator.placeholder.ecoFriendly" | transloco }}
                        </div>
                      }
                    </div>

                    <div class="flex flex-col gap-1">
                      <div class="flex items-center text-paragraph-s-med">
                        <span class="text-greyscale-600">
                          {{ "calculator.label.peoplePerRoom" | transloco }}:
                        </span>
                        <span class="ml-1 text-greyscale-900">
                          {{ accommodation.peoplePerRoom || "-" }}
                          {{ accommodation.peoplePerRoom ? (accommodation.peoplePerRoom > 1 ? ("travelBoards.people" | transloco) : ("travelBoards.person" | transloco)) : "" }}
                        </span>
                      </div>
                      <div class="flex text-paragraph-s-med">
                        <span class="text-greyscale-600">
                          {{ "calculator.placeholder.nights" | transloco }}:
                        </span>
                        <span class="ml-1 text-greyscale-900">
                          {{ accommodation.nights || "-" }}
                          {{ accommodation.nights ? (accommodation.nights > 1 ? ("prestations.nights" | transloco) : ("prestations.night" | transloco)) : "" }}
                        </span>
                      </div>
                    </div>
                  </article>
                }
              </div>
            } @else {
              <div class="text-paragraph-s-reg italic text-greyscale-500">
                {{ "calculator.aiAnalysisResults.noAccommodationDetected" | transloco }}
              </div>
            }
          </div>
        </div>
      </section>

      <!-- Section Info Globale -->
      <section class="flex flex-col gap-4">
        <h2 class="text-primary-900">{{ "calculator.globalInfo" | transloco }}</h2>

        <div class="flex flex-col gap-6 sm:flex-row">
          <!-- Meals -->
          <div class="flex flex-1 flex-col gap-4 rounded-lg bg-primary-50 p-4">
            <h3 class="text-primary-900">{{ "calculator.meal" | transloco }}</h3>

            @if (hasMeals) {
              <div class="flex flex-col gap-4">
                @for (
                  meal of brochureAnalysisService.state().result?.meals;
                  track $index
                ) {
                  <article class="flex flex-row gap-3 rounded-lg bg-white p-4">
                    <div class="rounded-lg bg-primary-50 p-3 text-heading-4 text-primary-950">
                      {{ meal.mealPercentage ? (meal.mealPercentage * 100 | number: '1.0-0') : "-" }}%
                    </div>
                    <div class="flex flex-1 flex-col gap-1">
                      <div class="paragraph-med text-primary-950">
                        {{ getMealTypeLabel(meal.type || "") }}
                      </div>
                      @if (meal.localSeasonalPercent) {
                        <div class="text-paragraph-xs-reg text-greyscale-700">
                          <span class="ml-1">{{ meal.localSeasonalPercent ? (meal.localSeasonalPercent * 100 | number: '1.0-0') : "-" }}%</span>
                          <span> {{ "prestation.localAndSeasonalDashboard" | transloco }}</span>
                        </div>
                      }
                    </div>
                  </article>
                }
              </div>
            } @else {
              <div class="text-paragraph-s-reg italic text-greyscale-500">
                {{ "calculator.aiAnalysisResults.noMealDetected" | transloco }}
              </div>
            }
          </div>

          <!-- Waste -->
          <div class="flex flex-1 flex-col gap-4 rounded-lg bg-primary-50 p-4">
            <h3 class="text-primary-900">{{ "calculator.waste" | transloco }}</h3>

            @if (hasWaste) {
              <article class="flex flex-col gap-3 rounded-lg bg-white p-4">
                <div class="paragraph-med text-primary-950">
                  {{ getWasteTypeLabel(brochureAnalysisService.state().result?.wasteType || "") }}
                </div>
                <div class="flex flex-col gap-1 text-paragraph-s-med">
                  <div class="flex">
                    <span class="text-greyscale-600">{{ "calculator.label.kgOfPlastic" | transloco }}:</span>
                    <span class="ml-1 text-greyscale-900">
                      {{ brochureAnalysisService.state().result?.kgOfPlastic || "-" }}
                      {{ brochureAnalysisService.state().result?.kgOfPlastic ? "kg/" + ("travelBoards.day" | transloco) + "/" + ("travelBoards.person" | transloco) : "" }}
                    </span>
                  </div>
                  <div class="flex">
                    <span class="text-greyscale-600">{{ "calculator.label.kgOfNonPlastic" | transloco }}:</span>
                    <span class="ml-1 text-greyscale-900">
                      {{ brochureAnalysisService.state().result?.kgOfNonPlastic || "-" }}
                      {{ brochureAnalysisService.state().result?.kgOfNonPlastic ? "kg/" + ("travelBoards.day" | transloco) + "/" + ("travelBoards.person" | transloco) : "" }}
                    </span>
                  </div>
                </div>
              </article>
            } @else {
              <div class="text-paragraph-s-reg italic text-greyscale-500">
                {{ "calculator.aiAnalysisResults.noWasteDetected" | transloco }}
              </div>
            }
          </div>
        </div>
      </section>
    </div>
  }

  <ng-template pTemplate="footer">
    @if (
      brochureAnalysisService.state().status === "success" &&
      brochureAnalysisService.state().result
    ) {
      <div class="mt-4 flex justify-between">
        <p-button
          label="{{ 'calculator.backToCalculator' | transloco }}"
          styleClass="p-button-secondary"
          (onClick)="brochureAnalysisService.cancelAnalysis()"
        ></p-button>
        <p-button
          label="{{ 'calculator.aiAnalysisResults.useData' | transloco }}"
          (onClick)="brochureAnalysisService.useData()"
          class="ml-4"
        ></p-button>
      </div>
    }
  </ng-template>
</p-dialog>
